import { InfoOutlineIcon } from '@carboncopyco/icons';
import type { TopAsset, TopAssetType } from '@carboncopyco/types';
import {
  ColorHex,
  TextCaption,
  Tooltip,
  TooltipBubble,
  Typography,
} from '@carboncopyco/ui';
import classNames from 'classnames';
import { useMemo, useRef } from 'react';
import { useConsoleState } from './ConsoleContext';
import { iconByAssetType, iconTitleByAssetType } from './consts';
import { useAssetStatus } from './useAssetStatus';

type GetDisableMessageOptions = {
  assetType: TopAssetType;
  reasonToDisable?: 'isDeleted' | 'isInactive';
};

export const getDisableMessage = ({
  assetType,
  reasonToDisable,
}: GetDisableMessageOptions) =>
  reasonToDisable === 'isDeleted'
    ? `This ${assetType} was archived. Contact an admin to request access.`
    : reasonToDisable === 'isInactive'
      ? `You no longer have access to this ${assetType}. Contact an admin for permission.`
      : '';

type AssetNameLinkProps = { topAsset: TopAsset };

export const AssetNameLink = ({
  topAsset,
  topAsset: { assetId, workspaceId, assetType, name },
}: AssetNameLinkProps) => {
  const { isDisabled, reasonToDisable } = useAssetStatus({
    asset: topAsset,
  });

  const { goToAssetPage } = useConsoleState();
  const Icon = iconByAssetType[assetType];

  const infoIconRef = useRef();
  const nameRef = useRef<any>();

  const textColor = useMemo(
    () => (isDisabled ? 'text-ds-natural-300' : 'text-ds-natural-600'),
    [isDisabled]
  );

  return (
    <div className="flex items-center gap-2.5 w-full overflow-hidden">
      <div className="shrink-0">
        <TooltipBubble
          title={iconTitleByAssetType[assetType]}
          placement="top"
        >
          <Icon />
        </TooltipBubble>
      </div>

      <div className="flex items-center justify-between gap-2 overflow-hidden">
        <div
          ref={nameRef}
          onClick={() => {
            goToAssetPage({
              id: assetId,
              workspaceId,
              type: assetType,
            });
          }}
          className={classNames(
            'truncate',
            textColor,
            isDisabled ? 'pointer-events-none ' : 'cursor-pointer'
          )}
        >
          <Typography
            color={textColor}
            label={name}
            fontWeight="font-ds-normal"
          />
        </div>

        <TooltipBubble
          anchorRef={nameRef}
          title={name}
          placement="top"
        />

        {isDisabled && (
          <>
            <div
              className="shrink-0"
              ref={infoIconRef}
            >
              <InfoOutlineIcon
                size={16}
                color={ColorHex.natural300}
              />
            </div>
            <Tooltip
              delay={[300, 0]}
              interactive={false}
              noArrow
              placement="right-start"
              anchorRef={infoIconRef}
              zIndex={9999}
            >
              <div className="p-3 bg-ds-accent3-100 rounded-md w-48 flex whitespace-normal">
                <TextCaption
                  label={getDisableMessage({
                    reasonToDisable,
                    assetType,
                  })}
                />
              </div>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};
